/**
 * components/brief-builder/BriefConfigStep.tsx
 *
 * Step 4 — Optional AI & Module Configuration.
 * Roles: Solution Architect / Account Manager
 *
 * Allows configuration of:
 *   - AI Assistant: prompt ID, vector store, wizard prompt, starting questions
 *   - Survey Module (if enabled): prompt IDs, survey types, number of questions
 *
 * All fields are pre-filled with platform defaults. The user can skip this step
 * and go directly to export.
 */
import { useState, useCallback } from 'react'
import type {
  BriefSession,
  AIAssistantConfig,
  SurveyModuleConfig,
  SubmitModuleConfigPayload,
} from '@/types/brief-builder'
import { SURVEY_ALLOWED_TYPES } from '@/types/brief-builder'
import { briefBuilder } from '@/api/client'

// ── Defaults ──────────────────────────────────────────────────────────────────

const DEFAULT_AI: AIAssistantConfig = {
  prompt_id:          '',
  vector_store_id:    '',
  wizard_prompt_id:   '',
  starting_questions: [],
}

const DEFAULT_SURVEY: SurveyModuleConfig = {
  creator_prompt_id:  '',
  analyzer_prompt_id: '',
  survey_types:       ['Consumer Behaviour'],
  num_questions:      5,
}

interface Props {
  session: BriefSession
  onSave: (payload: SubmitModuleConfigPayload) => void
  loading?: boolean
}

// ── Section collapse wrapper ──────────────────────────────────────────────────

function Section({
  title,
  badge,
  defaultOpen = true,
  children,
}: {
  title: string
  badge?: string
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 bg-slate-50 border-b border-slate-100
                   hover:bg-slate-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
          {badge && (
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-600 border border-indigo-200">
              {badge}
            </span>
          )}
        </div>
        <svg
          width="14" height="14" viewBox="0 0 14 14" fill="none"
          className={`transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <path d="M3 5l4 4 4-4" stroke="currentColor" strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {open && <div className="px-5 py-4 space-y-4">{children}</div>}
    </div>
  )
}

// ── Field helpers ─────────────────────────────────────────────────────────────

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-semibold text-slate-700">
        {label}
        {hint && <span className="ml-1.5 text-slate-400 font-normal">{hint}</span>}
      </label>
      {children}
    </div>
  )
}

function PromptInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input
      className="w-full text-xs font-mono border border-slate-200 rounded-lg px-3 py-2
                 focus:outline-none focus:ring-2 focus:ring-indigo-300 bg-slate-50"
      value={value}
      onChange={e => onChange(e.target.value)}
    />
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function BriefConfigStep({ session, onSave, loading }: Props) {
  const modules    = session.approved_structure?.modules ?? ['dashboards', 'ai_assistant']
  const hasSurveys = modules.includes('surveys')

  // ── AI Assistant state ─────────────────────────────────────────────────────
  const [ai, setAi] = useState<AIAssistantConfig>(() => ({
    ...DEFAULT_AI,
    ...(session.ai_assistant_config ?? {}),
  }))
  const patchAi = (patch: Partial<AIAssistantConfig>) =>
    setAi(prev => ({ ...prev, ...patch }))

  // ── Starter questions ──────────────────────────────────────────────────────
  const [newQ,        setNewQ]        = useState('')
  const [suggesting,  setSuggesting]  = useState(false)
  const [suggestErr,  setSuggestErr]  = useState<string | null>(null)

  const addQuestion = () => {
    const q = newQ.trim()
    if (!q) return
    patchAi({ starting_questions: [...ai.starting_questions, q] })
    setNewQ('')
  }

  const removeQuestion = (idx: number) =>
    patchAi({ starting_questions: ai.starting_questions.filter((_, i) => i !== idx) })

  const suggestQuestions = useCallback(async () => {
    setSuggesting(true)
    setSuggestErr(null)
    try {
      const res = await briefBuilder.suggestQuestions(session.id)
      if (res.questions.length) {
        patchAi({ starting_questions: [...ai.starting_questions, ...res.questions] })
      }
      // Also update survey types if suggestions were returned and surveys are enabled
      if (hasSurveys && res.survey_types && res.survey_types.length) {
        patchSurvey({ survey_types: res.survey_types })
      }
    } catch (e) {
      setSuggestErr('Could not generate suggestions. Try again.')
    } finally {
      setSuggesting(false)
    }
  }, [session.id, ai.starting_questions, hasSurveys])

  // ── Survey state ───────────────────────────────────────────────────────────
  const [survey, setSurvey] = useState<SurveyModuleConfig>(() => ({
    ...DEFAULT_SURVEY,
    ...(session.survey_config ?? {}),
  }))
  const patchSurvey = (patch: Partial<SurveyModuleConfig>) =>
    setSurvey(prev => ({ ...prev, ...patch }))

  const toggleSurveyType = (t: string) =>
    setSurvey(prev => ({
      ...prev,
      survey_types: prev.survey_types.includes(t)
        ? prev.survey_types.filter(x => x !== t)
        : [...prev.survey_types, t],
    }))

  // ── Save handlers ──────────────────────────────────────────────────────────
  const handleSave = () => {
    onSave({
      ai_assistant_config: ai,
      survey_config: hasSurveys ? survey : undefined,
    })
  }

  const handleSkip = () => {
    onSave({})   // empty payload → backend uses all defaults
  }

  return (
    <div className="max-w-3xl mx-auto px-5 py-10 space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-900">AI & Module Configuration</h2>
        <p className="text-sm text-slate-500 mt-1">
          Configure prompt IDs and settings for AI modules. All fields are pre-filled with
          platform defaults — skip if unsure.
        </p>
      </div>

      {/* AI Assistant */}
      <Section title="AI Assistant" badge="always on">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Assistant Prompt ID">
            <PromptInput value={ai.prompt_id} onChange={v => patchAi({ prompt_id: v })} />
          </Field>
          <Field label="Vector Store ID">
            <PromptInput value={ai.vector_store_id} onChange={v => patchAi({ vector_store_id: v })} />
          </Field>
        </div>
        <Field label="Wizard Prompt ID">
          <PromptInput value={ai.wizard_prompt_id} onChange={v => patchAi({ wizard_prompt_id: v })} />
        </Field>

        {/* Starting questions */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-slate-700">
              Starting Questions
              <span className="ml-1.5 text-slate-400 font-normal">
                (appear by default in the chatbot)
              </span>
            </label>
            <button
              onClick={suggestQuestions}
              disabled={suggesting}
              className="text-[11px] text-indigo-600 hover:text-indigo-800 font-medium
                         disabled:opacity-40 flex items-center gap-1"
            >
              {suggesting ? (
                <>
                  <span className="w-3 h-3 rounded-full border-2 border-indigo-400
                                   border-t-transparent animate-spin" />
                  Generating…
                </>
              ) : (
                <>✦ AI Suggest</>
              )}
            </button>
          </div>

          {suggestErr && (
            <p className="text-[11px] text-red-500">{suggestErr}</p>
          )}

          {/* Existing questions */}
          <div className="space-y-1.5">
            {ai.starting_questions.map((q, i) => (
              <div key={i} className="flex items-start gap-2 bg-slate-50 rounded-lg px-3 py-2">
                <span className="text-slate-300 text-xs mt-0.5 shrink-0">{i + 1}.</span>
                <p className="text-xs text-slate-700 flex-1 leading-relaxed">{q}</p>
                <button
                  onClick={() => removeQuestion(i)}
                  className="text-slate-300 hover:text-red-400 text-xs shrink-0 mt-0.5"
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          {/* Add question inline */}
          <div className="flex gap-2">
            <input
              className="flex-1 text-xs border border-slate-200 rounded-lg px-3 py-2
                         focus:outline-none focus:ring-2 focus:ring-indigo-300"
              placeholder="Type a starting question and press Enter…"
              value={newQ}
              onChange={e => setNewQ(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addQuestion() } }}
            />
            <button
              onClick={addQuestion}
              disabled={!newQ.trim()}
              className="px-3 py-1.5 text-xs rounded-lg bg-indigo-100 text-indigo-700 font-medium
                         hover:bg-indigo-200 disabled:opacity-40 transition-colors"
            >
              + Add
            </button>
          </div>
        </div>
      </Section>

      {/* Survey Module */}
      {hasSurveys && (
        <Section title="Survey Module" badge="surveys enabled">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Creator Prompt ID">
              <PromptInput
                value={survey.creator_prompt_id}
                onChange={v => patchSurvey({ creator_prompt_id: v })}
              />
            </Field>
            <Field label="Analyzer Prompt ID">
              <PromptInput
                value={survey.analyzer_prompt_id}
                onChange={v => patchSurvey({ analyzer_prompt_id: v })}
              />
            </Field>
          </div>

          {/* Survey types */}
          <Field label="Survey Types" hint="(select all that apply)">
            <div className="flex flex-wrap gap-2 mt-1">
              {SURVEY_ALLOWED_TYPES.map(t => {
                const active = survey.survey_types.includes(t)
                return (
                  <button
                    key={t}
                    onClick={() => toggleSurveyType(t)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors
                      ${active
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'
                      }`}
                  >
                    {t}
                  </button>
                )
              })}
            </div>
          </Field>

          {/* Number of questions */}
          <Field label="Number of Questions per Survey" hint={`(${survey.num_questions})`}>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-xs text-slate-400 w-4">3</span>
              <input
                type="range"
                min={3}
                max={8}
                step={1}
                value={survey.num_questions}
                onChange={e => patchSurvey({ num_questions: parseInt(e.target.value) })}
                className="flex-1 accent-indigo-600"
              />
              <span className="text-xs text-slate-400 w-4">8</span>
              <span className="text-sm font-semibold text-indigo-700 w-6 text-center">
                {survey.num_questions}
              </span>
            </div>
          </Field>
        </Section>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleSkip}
          disabled={loading}
          className="px-4 py-2 rounded-lg border border-slate-200 text-slate-500 text-sm
                     font-medium hover:bg-slate-50 disabled:opacity-40 transition-colors"
        >
          Skip (use defaults)
        </button>
        <button
          onClick={handleSave}
          disabled={loading}
          className="px-6 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold
                     hover:bg-indigo-700 disabled:opacity-40 transition-colors"
        >
          {loading ? 'Saving…' : 'Save & Continue to Export →'}
        </button>
      </div>
    </div>
  )
}
